import { css } from "@emotion/react";
import { addDays, format, isSameDay, isValid, max, parseISO } from "date-fns";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Icon } from "@zapier/zinnia-icons";
import {
  Avatar,
  Button,
  IconButton,
  ServiceIcon,
  Spacer,
  Text,
} from "@zapier/design-system";

import {
  DateFilter,
  type DisabledDates,
} from "@zapier/reporting/src/components/global/DateFilter";
import { UserFilter } from "@zapier/reporting/src/components/global/UserFilter";
import { useAccountAndUser, useSize } from "@zapier/reporting/src/hooks";
import {
  FilterChangeType,
  HistoryFiltersTypes,
} from "@zapier/reporting/src/types";
import {
  getResponsiveStyles,
  visibleOnlyOnMobile,
} from "@zapier/reporting/src/utils";
import { dateRangeLabel, getDateOptions } from "../getDateOptions";
import { getEarliestSelectableDate } from "../getEarliestSelectableDate";
import SearchInput from "../SearchInput";
import AppFilter from "./AppFilter";
import { badgePortalId, FilterBadges } from "./constants";
import { FilterChip } from "./FilterChip";
import { HistoryFilterControl, useFilterControlState } from "./filterControls";
import FolderFilter from "./FolderFilter";
import ZapFilter from "./ZapFilter";

type Props = {
  hiddenFilters?: HistoryFilterControl[];
  filters: HistoryFiltersTypes;
  maxDateRangeDays?: number;
  onClearFilters: () => void;
  onFilterChange: FilterChangeType;
  onRefreshData: () => void;
  defaultDateKey?: string;
};

const rootStyle = getResponsiveStyles({
  desktop: css`
    grid-template-rows: auto 1fr;
  `,
  mobile: css`
    display: grid;
    grid-gap: 20px;
  `,
});

const badgeRow = css`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  & [data-type="${FilterBadges.dateRange}"] {
    order: 1;
  }
  & [data-type="${FilterBadges.zap}"] {
    order: 2;
  }
  & [data-type="${FilterBadges.app}"] {
    order: 3;
  }
  & [data-type="${FilterBadges.folder}"] {
    order: 4;
  }
  & [data-type="${FilterBadges.owner}"] {
    order: 5;
  }
  & [data-type="${FilterBadges.clearAll}"] {
    order: 100;
  }
`;

const filtersStyle = getResponsiveStyles({
  // This is to make the DateRangeFilter collapse at the same rate as the other filters
  // For some reason the date filter allows itself to be squished easier than the others
  // https://zapier.slack.com/archives/CE56M4W05/p1643377543511289
  desktop: css`
    grid-template-rows: 1fr;
  `,
  tablet: css`
    align-items: flex-end;
    grid-template-rows: 1fr;
    max-height: 100vh;
    overflow: initial;
  `,
  mobile: css`
    display: grid;
    grid-gap: 10px;
    max-height: 0px;
    overflow: hidden;
    transition: max-height 0.25s ease-in-out;
  `,
});

const filtersExpanded = getResponsiveStyles({
  mobile: css`
    overflow: initial;
    max-height: 100vh;
  `,
});

const getFilterColumnStyle = (
  desktopGridColumns: string,
  tabletGridColumns: string
) =>
  getResponsiveStyles({
    desktop: css`
      grid-template-columns: ${desktopGridColumns};
    `,
    tablet: css`
      grid-template-columns: ${tabletGridColumns};
    `,
  });

const searchInputStyle = getResponsiveStyles({
  tablet: css`
    display: flex;
    width: 30%;
  `,
  mobile: css`
    display: flex;
    width: 100%;
  `,
});

const defaultHiddenFilters: HistoryFilterControl[] = [];

function Filters({
  hiddenFilters = defaultHiddenFilters,
  defaultDateKey,
  filters,
  maxDateRangeDays,
  onClearFilters,
  onFilterChange,
  onRefreshData,
}: Props) {
  const t = useTranslations("history-page.filters");
  const { accountId, currentUsageCycle, isTeam, userId } = useAccountAndUser();
  const [showFilters, setShowFilters] = useState(false);
  const size = useSize();
  const controlState = useFilterControlState(hiddenFilters, Boolean(isTeam));

  const filterColumnStyle = useMemo(
    () =>
      getFilterColumnStyle(
        controlState.desktopGridColumns,
        controlState.tabletGridColumns
      ),
    [controlState.desktopGridColumns, controlState.tabletGridColumns]
  );

  const dateOptions = useMemo(
    () =>
      getDateOptions(
        currentUsageCycle
          ? {
              from: new Date(currentUsageCycle.from),
              to: new Date(currentUsageCycle.to),
            }
          : {}
      ),
    [currentUsageCycle]
  );

  // The picker treats a committed single day as a start that is waiting for an
  // end day, so the cap has to apply before the first click
  const committedSingleDay = useMemo(() => {
    const from = parseISO(filters.dateFilter.from);
    const to = parseISO(filters.dateFilter.to);
    return isValid(from) && isValid(to) && isSameDay(from, to)
      ? from
      : undefined;
  }, [filters.dateFilter.from, filters.dateFilter.to]);

  // The day the picker is waiting for an end day for. `undefined` follows the
  // committed single day, `null` means no start is waiting.
  const [anchorDate, setAnchorDate] = useState<Date | null>();
  const pendingStartDate =
    anchorDate === undefined ? committedSingleDay : (anchorDate ?? undefined);

  const handleDateRangeCap = useCallback(
    (day: Date) => {
      if (!maxDateRangeDays) {
        return;
      }

      // A click while a start is waiting either completes the range or clears
      // the start day, so nothing is waiting afterwards. Otherwise it starts one.
      setAnchorDate((current) => {
        const pending = current === undefined ? committedSingleDay : current;
        return pending ? null : day;
      });
    },
    [committedSingleDay, maxDateRangeDays]
  );

  // Clear picker bounds after dates are selected. This allows users to start a
  // new date range selection without having to manually clear the previous one
  useEffect(() => {
    if (!maxDateRangeDays) {
      return;
    }

    setAnchorDate(undefined);
  }, [filters.dateFilter.from, filters.dateFilter.to, maxDateRangeDays]);

  const earliestDateTime = getEarliestSelectableDate().getTime();

  const disabledDates = useMemo<DisabledDates>(() => {
    const earliest = new Date(earliestDateTime);
    if (!maxDateRangeDays || !pendingStartDate) {
      return { before: earliest };
    }

    const maxDayDiff = maxDateRangeDays - 1;
    return {
      after: addDays(pendingStartDate, maxDayDiff),
      before: max([earliest, addDays(pendingStartDate, -maxDayDiff)]),
    };
  }, [earliestDateTime, maxDateRangeDays, pendingStartDate]);

  return (
    <div>
      <div css={rootStyle}>
        <div css={visibleOnlyOnMobile}>
          <Text type="SmallPrint4">{t("filters-label")}</Text>
          <Spacer height="5px" />
        </div>
        {controlState.showSearch && (
          <div css={searchInputStyle}>
            <SearchInput
              isFullWidth
              isSearching={false}
              onChange={(value) => onFilterChange("search", value)}
              value={filters.search}
            />
          </div>
        )}
        <div css={visibleOnlyOnMobile}>
          <Button
            variant="secondary"
            onPress={() => setShowFilters(!showFilters)}
          >
            {showFilters ? t("hide-filters") : t("show-filters")}
          </Button>
          <Spacer height="10px" />
        </div>
        <div
          css={[
            filtersStyle,
            filterColumnStyle,
            showFilters && filtersExpanded,
          ]}
        >
          {controlState.showInline.date && (
            <DateFilter
              defaultOptionKey={defaultDateKey}
              id="HistoryDateFilter"
              onChange={(value) => {
                if (maxDateRangeDays) {
                  setAnchorDate(undefined);
                }

                onFilterChange("dateFilter", value);
              }}
              options={dateOptions}
              size={size}
              value={filters.dateFilter}
              disabledDates={disabledDates}
              onDayClick={maxDateRangeDays ? handleDateRangeCap : undefined}
            >
              {({ from, hasValue, to }) => {
                const label = dateRangeLabel(from, to, dateOptions);
                const dateFormat = size === "large" ? "MM/dd/yyyy" : "MMM d";
                const dateRange = `${format(
                  parseISO(from),
                  dateFormat
                )} - ${format(parseISO(to), dateFormat)}`;

                return (
                  <FilterChip
                    image={<Icon name="actionCalendar" size={24} />}
                    onClear={
                      hasValue
                        ? () =>
                            onFilterChange("dateFilter", { hasChange: true })
                        : undefined
                    }
                    type={FilterBadges.dateRange}
                    value={dateRange}
                  >
                    <Text type="Body4">{label && `${label}: `}</Text>
                    <Text type="Body1">{dateRange}</Text>
                  </FilterChip>
                );
              }}
            </DateFilter>
          )}
          {controlState.showInline.zap && (
            <ZapFilter
              accountId={accountId}
              id="HistoryZapFilter"
              onChange={(value) => {
                onFilterChange("nodeIds", [
                  ...new Set([
                    ...(filters.nodeIds as string[]),
                    value as string,
                  ]),
                ]);
              }}
              value={filters.nodeIds}
            >
              {({ selectedZaps, zapIds }) =>
                zapIds.map((zapId) => {
                  const zap = selectedZaps.find(
                    (zap) => String(zap.id) === zapId
                  );
                  return (
                    <FilterChip
                      image={
                        zap ? (
                          <Icon
                            name={
                              zap?.kind === "transfer"
                                ? "arrowConnect"
                                : "miscBoltAlt"
                            }
                            size={24}
                          />
                        ) : undefined
                      }
                      key={zapId}
                      onClear={() =>
                        onFilterChange(
                          "nodeIds",
                          [...(filters.nodeIds || [])].filter(
                            (id) => id !== zapId
                          )
                        )
                      }
                      type={FilterBadges.zap}
                      value={zap?.id ? zap.title : zapId}
                    />
                  );
                })
              }
            </ZapFilter>
          )}
          {controlState.showInline.app && (
            <AppFilter
              id="HistoryAppFilter"
              onChange={(value) =>
                onFilterChange("apps", [
                  ...new Set([...(filters.apps as string[]), value as string]),
                ])
              }
              value={filters.apps}
            >
              {({ appIds, selectedApps }) =>
                appIds.map((appId) => {
                  const app = selectedApps.find(
                    (a) => a.currentImplementation?.id === appId
                  );
                  return (
                    <FilterChip
                      image={
                        app ? (
                          <ServiceIcon
                            aria-hidden={true}
                            serviceName={app?.name}
                            size="small"
                            src={app?.images?.url16X16 || ""}
                          />
                        ) : undefined
                      }
                      key={appId}
                      onClear={() =>
                        onFilterChange(
                          "apps",
                          [...(filters.apps || [])].filter((id) => id !== appId)
                        )
                      }
                      type={FilterBadges.app}
                      value={app?.name || appId}
                    />
                  );
                })
              }
            </AppFilter>
          )}
          {controlState.showInline.folder && (
            <FolderFilter
              accountId={accountId}
              id="HistoryFolderFilter"
              onChange={(value) =>
                onFilterChange("folderIds", [
                  ...new Set([
                    ...(filters.folderIds as string[]),
                    value as string,
                  ]),
                ])
              }
              value={filters.folderIds}
            >
              {({ folderIds, selectedFolders }) =>
                folderIds.map((folderId) => {
                  const folder = selectedFolders.find(
                    (f) => String(f.id) === folderId
                  );
                  return (
                    <FilterChip
                      image={
                        folder ? <Icon name="navFolder" size={24} /> : undefined
                      }
                      key={folderId}
                      onClear={() =>
                        onFilterChange(
                          "folderIds",
                          [...(filters.folderIds || [])].filter(
                            (id) => String(id) !== String(folderId)
                          )
                        )
                      }
                      type={FilterBadges.folder}
                      value={folder?.title || folderId}
                    />
                  );
                })
              }
            </FolderFilter>
          )}
          {controlState.showInline.owner && (
            <UserFilter
              accountId={accountId}
              allowMultipleSelections={true}
              ariaLabel={t("owner-search-aria-label")}
              id="HistoryUserFilter"
              menuAriaLabel={t("owner-menu-aria-label")}
              onChange={(value) =>
                onFilterChange("ownerIds", [
                  ...new Set([
                    ...(filters.ownerIds as string[]),
                    String(value),
                  ]),
                ])
              }
              placeholder={t("owner-placeholder")}
              userId={userId}
              value={filters.ownerIds}
            >
              {({ memberIds, selectedMembers }) =>
                memberIds.map((memberId) => {
                  const member = selectedMembers.find(
                    (m) => String(m.recordId) === memberId
                  );
                  return (
                    <FilterChip
                      image={
                        member ? (
                          <Avatar
                            aria-hidden={true}
                            name={String(member.name)}
                            size="small"
                            url={member.imageUrl}
                          />
                        ) : undefined
                      }
                      key={memberId}
                      onClear={() =>
                        onFilterChange(
                          "ownerIds",
                          [...(filters.ownerIds || [])].filter(
                            (id) => String(id) !== String(memberId)
                          )
                        )
                      }
                      type={FilterBadges.owner}
                      value={member?.name || memberId}
                    />
                  );
                })
              }
            </UserFilter>
          )}
          {controlState.showInline.refresh && (
            <IconButton
              variant="tertiary"
              name="actionAutoRenew"
              aria-label={t("refresh-aria-label")}
              onPress={onRefreshData}
              size="medium"
            />
          )}
        </div>
        <div css={badgeRow} id={badgePortalId}>
          <div data-type="Clear All">
            <Button variant="ghost" onPress={onClearFilters}>
              {t("clear-all")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Filters;
